import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import adminAuthService from '../services/adminAuthService';

const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);

  // Load admin profile
  const loadAdminProfile = async (authUser) => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authUser.id)
        .single();

      if (error || !profile) {
        return null;
      }

      // Verify admin privileges
      if (!profile.is_admin && profile.role !== 'admin' && profile.role !== 'superadmin') {
        return null;
      }

      return {
        id: authUser.id,
        email: profile.email,
        name: profile.name,
        role: profile.role,
        is_admin: profile.is_admin
      };
    } catch (error) {
      console.error('Error loading admin profile:', error);
      return null;
    }
  };

  useEffect(() => {
    const getInitialSession = async () => {
      try {
        setLoading(true);

        // Check local admin auth state first
        if (adminAuthService.isAuthenticated()) {
          const localAdmin = adminAuthService.getCurrentAdmin();
          const localSession = adminAuthService.getCurrentSession();
          if (localAdmin && localSession) {
            setAdmin(localAdmin);
            setSession(localSession);
            setLoading(false);
            return;
          }
        }

        // Check Supabase session for admin
        const { data: { session: supabaseSession } } = await supabase.auth.getSession();
        if (supabaseSession?.user) {
          const profile = await loadAdminProfile(supabaseSession.user);
          if (profile) {
            setSession(supabaseSession);
            setAdmin(profile);
          }
        }
      } catch (error) {
        console.error('Admin auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };
    getInitialSession();
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setTimeout(async () => {
        if (event === 'SIGNED_IN' && session?.user) {
          const profile = await loadAdminProfile(session.user);
          if (profile) {
            setSession(session);
            setAdmin(profile);
          }
        } else if (event === 'SIGNED_OUT') {
          setSession(null);
          setAdmin(null);
        }
        setLoading(false);
      }, 0);
    });
    return () => subscription.unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      const adminProfile = await adminAuthService.loginAdmin(email, password);
      setAdmin(adminProfile);
      setSession(adminAuthService.getCurrentSession());
      return adminProfile;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      console.log('Starting admin logout...');
      await adminAuthService.logout();
      console.log('Admin service logout complete');
      
      // Also sign out from Supabase
      try {
        await supabase.auth.signOut();
        console.log('Supabase signout complete');
      } catch (supabaseError) {
        console.error('Supabase signout error:', supabaseError);
      }
      
      setAdmin(null);
      setSession(null);
      console.log('Admin state cleared');
    } catch (error) {
      console.error('Admin logout error:', error);
      // Force clear state even if there's an error
      setAdmin(null);
      setSession(null);
    }
  };

  return (
    <AdminAuthContext.Provider value={{ admin, loading, session, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export default AdminAuthContext;
